import {
    signInWithPopup,
    signInWithRedirect,
    getRedirectResult,
    signInWithCredential,
    updateProfile,
    OAuthProvider,
} from 'firebase/auth';
import { httpsCallable } from 'firebase/functions';
import {
    auth,
    googleProvider,
    appleProvider,
    facebookProvider,
    db,
    functions,
} from './firebase';
import { isIosApp, isNativeApp } from './utils/nativePlatform';
import {
    createRawNonce,
    decodeJwtPayload,
    getNativeAuthPlugin,
    looksLikeJwt,
    pollUntilAuthResult,
    sha256Hex,
} from './utils/socialAuthPlugins';
import { APPLE_AUTH, FACEBOOK_AUTH, isFacebookAuthConfigured } from './utils/authConfig';
import { buildDisplayNameFromParts } from './utils/authDisplay';
import {
    collection,
    doc,
    getDoc,
    getDocs,
    limit,
    orderBy,
    query,
    runTransaction,
    setDoc,
    serverTimestamp,
    where,
} from 'firebase/firestore';
import { isValidGid, randomGidCandidate } from './utils/gid';
import {
    buildPublicProfile,
    parsePublicProfileDoc,
} from './utils/publicProfile';
import {
    FRIEND_REQUEST_STATUS,
    normalizeFriendEdge,
    normalizeFriendRequest,
} from './utils/friends';
import {
    LEADERBOARD_TOP_N,
    normalizeLeaderboardEntry,
} from './utils/leaderboards';

const callable = (name) => httpsCallable(functions, name);

const FIREBASE_CREDENTIAL_TIMEOUT_MS = 25000;

const applyProviderProfile = async (user, { displayName, photoURL } = {}) => {
    if (!user) {
        return user;
    }
    const nextName = String(displayName || "").trim();
    const nextPhoto = String(photoURL || "").trim();
    const updates = {};
    const existingName = String(user.displayName || "").trim();
    const existingBad =
        !existingName ||
        existingName.toLowerCase() === "null" ||
        existingName.toLowerCase() === "undefined";
    if (nextName && nextName.toLowerCase() !== "null" && existingBad) {
        updates.displayName = nextName.slice(0, 48);
    }
    if (nextPhoto && !user.photoURL) {
        updates.photoURL = nextPhoto;
    }
    if (!Object.keys(updates).length) {
        return user;
    }
    try {
        await updateProfile(user, updates);
    } catch (error) {
        console.warn("Failed to update auth profile", error);
    }
    return user;
};

/** signInWithCredential can hang forever on Cap iOS if Auth persistence is wrong. */
const signInWithCredentialTimed = async (credential) => {
    let timeoutId = null;
    try {
        return await Promise.race([
            signInWithCredential(auth, credential),
            new Promise((_, reject) => {
                timeoutId = setTimeout(() => {
                    reject(
                        new Error(
                            "Firebase sign-in timed out. In Firebase Console → Authentication → Settings → Authorized domains, add localhost (and capacitor://localhost if listed). Then reinstall the TestFlight build."
                        )
                    );
                }, FIREBASE_CREDENTIAL_TIMEOUT_MS);
            }),
        ]);
    } finally {
        if (timeoutId) {
            clearTimeout(timeoutId);
        }
    }
};

const getCallableErrorMessage = (error, fallback) => {
    const raw = String(error?.message || error?.details || "");
    const firebaseMatch = raw.match(
        /^Firebase:\s*(.+?)\s*\(functions\/[a-z-]+\)\.?$/i
    );

    if (firebaseMatch && firebaseMatch[1]) {
        return firebaseMatch[1];
    }

    if (raw) {
        return raw;
    }

    return fallback;
};

export const signInWithGoogle = async () => {
    try {
        // Firebase redirect OAuth does not complete reliably inside Capacitor
        // WKWebView (stays on "Signing in…"). Use Apple / Facebook on device
        // until a native Google Auth plugin is wired.
        if (isNativeApp()) {
            throw new Error(
                "Google Sign-In is not available in the iOS app yet. Use Sign in with Apple or Facebook."
            );
        }

        const result = await signInWithPopup(auth, googleProvider);
        return result.user;
    } catch (error) {
        console.error("Error signing in with Google", error);
        throw error;
    }
};

/** Finish a pending Google redirect sign-in (no-op if none). */
export const completeGoogleRedirectSignIn = async () => {
    try {
        const result = await getRedirectResult(auth);
        return result?.user || null;
    } catch (error) {
        console.error("Error completing Google redirect sign-in", error);
        throw error;
    }
};

/**
 * Sign in with Apple.
 * - Native iOS: start + poll NativeAuth (Cap promises spanning sheets are unreliable)
 * - Web: Firebase Apple OAuth popup/redirect
 * @param {{ onStatus?: (stage: string) => void }} [options]
 */
export const signInWithApple = async (options = {}) => {
    const onStatus = typeof options.onStatus === "function" ? options.onStatus : null;
    try {
        if (isNativeApp() && isIosApp()) {
            const NativeAuth = getNativeAuthPlugin();
            if (!NativeAuth || typeof NativeAuth.startAppleSignIn !== "function") {
                throw new Error(
                    "Sign in with Apple plugin missing (NativeAuth). Install the latest TestFlight build."
                );
            }

            if (onStatus) onStatus("Opening Apple…");
            const rawNonce = createRawNonce();
            const hashedNonce = await sha256Hex(rawNonce);

            const started = await NativeAuth.startAppleSignIn({
                scopes: "email name",
                state: createRawNonce(16),
                nonce: hashedNonce,
            });
            if (!started?.started) {
                throw new Error("Apple sign-in failed to start.");
            }

            if (onStatus) onStatus("Waiting for Apple…");
            const appleResult = await pollUntilAuthResult({
                poll: () => NativeAuth.pollAppleSignIn(),
                onStatus: (status) => {
                    if (onStatus && status === "pending") {
                        onStatus("Waiting for Apple…");
                    }
                },
            });

            if (appleResult.status === "cancelled") {
                throw new Error(appleResult.error || "Apple sign-in was cancelled.");
            }
            if (appleResult.status !== "completed") {
                throw new Error(
                    appleResult.error || "Apple sign-in failed."
                );
            }

            const identityToken = appleResult?.response?.identityToken;
            if (!identityToken) {
                throw new Error("Apple did not return an identity token.");
            }

            if (onStatus) onStatus("Signing into Firebase…");
            const tokenClaims = decodeJwtPayload(identityToken);
            const tokenAudience = tokenClaims?.aud;

            const provider = new OAuthProvider("apple.com");
            const credential = provider.credential({
                idToken: identityToken,
                rawNonce,
            });

            try {
                const result = await signInWithCredentialTimed(credential);
                const response = appleResult?.response || {};
                const displayName = buildDisplayNameFromParts(
                    response.givenName,
                    response.familyName,
                    response.email || result.user?.email
                );
                await applyProviderProfile(result.user, { displayName });
                return result.user;
            } catch (firebaseError) {
                const aud =
                    typeof tokenAudience === "string"
                        ? tokenAudience
                        : JSON.stringify(tokenAudience || "unknown");
                const detail = String(
                    firebaseError?.message || firebaseError || ""
                );
                if (
                    /audience/i.test(detail) ||
                    /invalid-credential/i.test(String(firebaseError?.code || ""))
                ) {
                    const err = new Error(
                        `Apple token audience is "${aud}". In Firebase → Authentication → Sign-in method → Apple, set Services ID to exactly "${APPLE_AUTH.clientId}" (the iOS Bundle ID), Save, then try again.`
                    );
                    err.code = firebaseError?.code;
                    err.cause = firebaseError;
                    throw err;
                }
                throw firebaseError;
            }
        }

        if (isNativeApp()) {
            await signInWithRedirect(auth, appleProvider);
            return null;
        }

        const result = await signInWithPopup(auth, appleProvider);
        return result.user;
    } catch (error) {
        console.error("Error signing in with Apple", error);
        throw error;
    }
};

/**
 * Sign in with Facebook.
 * Native iOS: Limited Login via start + poll NativeAuth.
 * @param {{ onStatus?: (stage: string) => void }} [options]
 */
export const signInWithFacebook = async (options = {}) => {
    const onStatus = typeof options.onStatus === "function" ? options.onStatus : null;
    if (!isFacebookAuthConfigured()) {
        throw new Error(
            "Facebook Login is not configured yet. Add your Facebook App ID in src/utils/authConfig.js."
        );
    }

    try {
        if (isNativeApp()) {
            const NativeAuth = getNativeAuthPlugin();
            if (
                !NativeAuth ||
                typeof NativeAuth.startFacebookSignIn !== "function"
            ) {
                throw new Error(
                    "Facebook Login plugin missing (NativeAuth). Install the latest TestFlight build."
                );
            }

            if (onStatus) onStatus("Opening Facebook…");
            const rawNonce = createRawNonce();
            const started = await NativeAuth.startFacebookSignIn({
                permissions: FACEBOOK_AUTH.permissions,
                nonce: rawNonce,
            });
            if (!started?.started) {
                throw new Error("Facebook sign-in failed to start.");
            }

            if (onStatus) onStatus("Waiting for Facebook…");
            const fbResult = await pollUntilAuthResult({
                poll: () => NativeAuth.pollFacebookSignIn(),
                onStatus: (status) => {
                    if (onStatus && status === "pending") {
                        onStatus("Waiting for Facebook…");
                    }
                },
            });

            if (fbResult.status === "cancelled") {
                throw new Error(fbResult.error || "Facebook sign-in was cancelled.");
            }
            if (fbResult.status !== "completed") {
                throw new Error(fbResult.error || "Facebook sign-in failed.");
            }

            const idToken = fbResult?.idToken || null;
            if (!idToken || !looksLikeJwt(idToken)) {
                throw new Error(
                    "Facebook sign-in did not return a Limited Login token. Try again."
                );
            }

            if (onStatus) onStatus("Signing into Firebase…");
            const tokenClaims = decodeJwtPayload(idToken);
            try {
                const provider = new OAuthProvider("facebook.com");
                const credential = provider.credential({
                    idToken,
                    rawNonce,
                });
                const result = await signInWithCredentialTimed(credential);
                const displayName =
                    buildDisplayNameFromParts(
                        tokenClaims?.given_name || tokenClaims?.first_name,
                        tokenClaims?.family_name || tokenClaims?.last_name,
                        tokenClaims?.email ||
                            fbResult?.email ||
                            result.user?.email
                    ) ||
                    String(tokenClaims?.name || fbResult?.name || "").trim();
                const photoURL =
                    typeof tokenClaims?.picture === "string"
                        ? tokenClaims.picture
                        : "";
                await applyProviderProfile(result.user, {
                    displayName,
                    photoURL,
                });
                return result.user;
            } catch (firebaseError) {
                const detail = String(
                    firebaseError?.message ||
                        firebaseError?.code ||
                        firebaseError ||
                        ""
                );
                throw new Error(
                    `Facebook → Firebase failed: ${detail}. Check Firebase Authentication → Facebook has the same App ID and App Secret as Meta.`
                );
            }
        }

        const result = await signInWithPopup(auth, facebookProvider);
        return result.user;
    } catch (error) {
        console.error("Error signing in with Facebook", error);
        throw error;
    }
};

/** Alias: complete any pending OAuth redirect (Google / Apple web-native). */
export const completePendingRedirectSignIn = completeGoogleRedirectSignIn;

const GID_CLAIM_ATTEMPTS = 16;

const isCallableMissingOrUnavailable = (error) => {
    const code = String(error?.code || "");
    return (
        code.includes("not-found") ||
        code.includes("unavailable") ||
        code.includes("internal") ||
        code.includes("deadline-exceeded") ||
        code.includes("unimplemented")
    );
};

/**
 * Claim a unique 8-digit GID in Firestore (works without Cloud Functions deployed).
 * 1) Atomically create gids/{gid}
 * 2) Write users/{uid}.gid once the registry row exists
 */
export const claimUserGidInFirestore = async (uid) => {
    if (!uid) {
        throw new Error("Sign in required to receive a Game Identity.");
    }

    const userRef = doc(db, "users", uid);
    const existingSnap = await getDoc(userRef);
    const existingGid = existingSnap.exists() ? existingSnap.data().gid : null;

    if (isValidGid(existingGid)) {
        return Number(existingGid);
    }

    for (let attempt = 0; attempt < GID_CLAIM_ATTEMPTS; attempt += 1) {
        const candidate = randomGidCandidate();
        const gidRef = doc(db, "gids", String(candidate));

        let claimed = false;

        try {
            await runTransaction(db, async (tx) => {
                const gidSnap = await tx.get(gidRef);

                if (gidSnap.exists()) {
                    if (gidSnap.data()?.uid === uid) {
                        claimed = true;
                        return;
                    }

                    throw new Error("GID_TAKEN");
                }

                tx.set(gidRef, {
                    uid,
                    createdAt: serverTimestamp(),
                });
                claimed = true;
            });
        } catch (error) {
            if (String(error?.message || "").includes("GID_TAKEN")) {
                continue;
            }

            // Contention / already-exists style failures: try another candidate.
            continue;
        }

        if (!claimed) {
            continue;
        }

        await setDoc(
            userRef,
            {
                gid: candidate,
                updatedAt: serverTimestamp(),
            },
            { merge: true }
        );

        const verifySnap = await getDoc(userRef);
        const savedGid = verifySnap.exists() ? verifySnap.data().gid : null;

        if (isValidGid(savedGid)) {
            return Number(savedGid);
        }
    }

    throw new Error(
        "Could not allocate a unique Game Identity. Please try again."
    );
};

/**
 * Ensure the signed-in account has a unique 8-digit GID.
 * Prefers Cloud Function `ensureGid`, then falls back to Firestore claiming
 * (so GID works even before Functions are deployed).
 */
export const ensureUserGid = async (uid) => {
    try {
        const result = await callable("ensureGid")({});
        const gid = Number(result?.data?.gid);

        if (!isValidGid(gid)) {
            throw new Error("Received an invalid Game Identity from the server.");
        }

        return gid;
    } catch (error) {
        if (!uid || !isCallableMissingOrUnavailable(error)) {
            throw new Error(
                getCallableErrorMessage(
                    error,
                    "Could not set up your Game Identity."
                )
            );
        }

        console.warn(
            "ensureGid callable unavailable; claiming GID in Firestore instead.",
            error
        );

        return claimUserGidInFirestore(uid);
    }
};

export const sendFriendRequestByGid = async (gid) => {
    try {
        const result = await callable("sendFriendRequest")({ gid: Number(gid) });
        return result?.data || null;
    } catch (error) {
        throw new Error(
            getCallableErrorMessage(error, "Could not send friend request.")
        );
    }
};

export const acceptFriendRequestById = async (requestId) => {
    try {
        const result = await callable("acceptFriendRequest")({ requestId });
        return result?.data || null;
    } catch (error) {
        throw new Error(
            getCallableErrorMessage(error, "Could not accept friend request.")
        );
    }
};

export const denyFriendRequestById = async (requestId) => {
    try {
        const result = await callable("denyFriendRequest")({ requestId });
        return result?.data || null;
    } catch (error) {
        throw new Error(
            getCallableErrorMessage(error, "Could not deny friend request.")
        );
    }
};

export const cancelFriendRequestById = async (requestId) => {
    try {
        const result = await callable("cancelFriendRequest")({ requestId });
        return result?.data || null;
    } catch (error) {
        throw new Error(
            getCallableErrorMessage(error, "Could not cancel friend request.")
        );
    }
};

export const unfriendByUid = async (friendUid) => {
    try {
        const result = await callable("unfriend")({ friendUid });
        return result?.data || null;
    } catch (error) {
        throw new Error(getCallableErrorMessage(error, "Could not unfriend."));
    }
};

export const redeemCode = async (code) => {
    try {
        const result = await callable("redeemCode")({ code: String(code || "") });
        return result?.data || null;
    } catch (error) {
        throw new Error(
            getCallableErrorMessage(error, "Could not redeem that code.")
        );
    }
};

export const listIncomingFriendRequests = async (uid) => {
    if (!uid) return [];

    const q = query(
        collection(db, "friendRequests"),
        where("toUid", "==", uid),
        where("status", "==", FRIEND_REQUEST_STATUS.PENDING),
        orderBy("createdAt", "desc")
    );
    const snap = await getDocs(q);

    return snap.docs
        .map((item) => normalizeFriendRequest(item.id, item.data()))
        .filter(Boolean);
};

export const listOutgoingFriendRequests = async (uid) => {
    if (!uid) return [];

    const q = query(
        collection(db, "friendRequests"),
        where("fromUid", "==", uid),
        where("status", "==", FRIEND_REQUEST_STATUS.PENDING),
        orderBy("createdAt", "desc")
    );
    const snap = await getDocs(q);

    return snap.docs
        .map((item) => normalizeFriendRequest(item.id, item.data()))
        .filter(Boolean);
};

export const listFriends = async (uid) => {
    if (!uid) return [];

    const snap = await getDocs(collection(db, "users", uid, "friends"));
    const friends = snap.docs
        .map((item) => normalizeFriendEdge(item.id, item.data()))
        .filter(Boolean);

    friends.sort((a, b) =>
        String(a.username || a.gid || "").localeCompare(
            String(b.username || b.gid || "")
        )
    );

    return friends;
};

export const loadFriendsSocialState = async (uid) => {
    if (!uid) {
        return {
            incomingRequests: [],
            outgoingRequests: [],
            friends: [],
        };
    }

    const [incomingRequests, outgoingRequests, friends] = await Promise.all([
        listIncomingFriendRequests(uid),
        listOutgoingFriendRequests(uid),
        listFriends(uid),
    ]);

    return { incomingRequests, outgoingRequests, friends };
};

export const areUsersFriends = async (viewerUid, friendUid) => {
    if (!viewerUid || !friendUid || viewerUid === friendUid) {
        return false;
    }

    const edgeRef = doc(db, "users", viewerUid, "friends", friendUid);
    const edgeSnap = await getDoc(edgeRef);
    return edgeSnap.exists();
};

/**
 * Load a publicProfiles/{uid} snapshot for any signed-in viewer.
 * Used by Friends and Leaderboards profile navigation.
 */
export const getPublicProfile = async (viewerUid, targetUid) => {
    if (!viewerUid || !targetUid) {
        throw new Error("Missing player id.");
    }

    if (viewerUid === targetUid) {
        throw new Error("Use your own Profile to view your account.");
    }

    const profileRef = doc(db, "publicProfiles", targetUid);
    const profileSnap = await getDoc(profileRef);

    if (!profileSnap.exists()) {
        throw new Error(
            "This player has not published a profile yet. Ask them to sign in once."
        );
    }

    const parsed = parsePublicProfileDoc(targetUid, profileSnap.data());
    if (!parsed) {
        throw new Error("Player profile data is unavailable.");
    }

    return parsed;
};

/**
 * Load a friend's publicProfiles snapshot. Requires an existing friendship edge.
 * Prefer getPublicProfile for leaderboard / general profile viewing.
 */
export const getFriendPublicProfile = async (viewerUid, friendUid) => {
    if (!viewerUid || !friendUid) {
        throw new Error("Missing player id.");
    }

    if (viewerUid === friendUid) {
        throw new Error("Use your own Profile to view your account.");
    }

    const isFriend = await areUsersFriends(viewerUid, friendUid);
    if (!isFriend) {
        throw new Error("You can only view profiles of players on your Friends list.");
    }

    return getPublicProfile(viewerUid, friendUid);
};

export const submitLeaderboardScore = async (boardId, value) => {
    try {
        const result = await callable("submitLeaderboardScore")({
            boardId,
            value: Number(value),
        });
        return result?.data || null;
    } catch (error) {
        throw new Error(
            getCallableErrorMessage(error, "Could not submit leaderboard score.")
        );
    }
};

export const syncMyLeaderboardBests = async () => {
    try {
        const result = await callable("syncMyLeaderboardBests")({});
        return result?.data || null;
    } catch (error) {
        throw new Error(
            getCallableErrorMessage(
                error,
                "Could not sync leaderboard personal bests."
            )
        );
    }
};

const getLeaderboardLoadErrorMessage = (error) => {
    const code = String(error?.code || "");
    const raw = String(error?.message || error?.details || "");
    if (
        code.includes("permission-denied") ||
        /missing or insufficient permissions/i.test(raw)
    ) {
        return "Could not load leaderboard. Sign in and try again.";
    }

    return getCallableErrorMessage(
        error,
        "Could not load leaderboard. Sign in and try again — if this keeps happening, the Open Beta server may still be updating."
    );
};

export const listLeaderboardEntries = async (
    boardId,
    topN = LEADERBOARD_TOP_N
) => {
    if (!boardId) {
        return [];
    }

    const q = query(
        collection(db, "leaderboards", boardId, "entries"),
        orderBy("value", "desc"),
        limit(Math.max(1, Math.min(100, Number(topN) || LEADERBOARD_TOP_N)))
    );
    const snap = await getDocs(q);

    return snap.docs
        .map((item) => normalizeLeaderboardEntry(item.id, item.data()))
        .filter(Boolean);
};

export const getMyLeaderboardEntry = async (uid, boardId) => {
    if (!uid || !boardId) {
        return null;
    }

    const snap = await getDoc(doc(db, "leaderboards", boardId, "entries", uid));
    if (!snap.exists()) {
        return null;
    }

    return normalizeLeaderboardEntry(snap.id, snap.data());
};

/**
 * Load a board's top list + the signed-in player's entry.
 * Prefers Cloud Function `listLeaderboard` (Admin read) so client Firestore
 * rules cannot block the page, then falls back to direct reads.
 */
export const fetchLeaderboardBoard = async (
    boardId,
    uid,
    topN = LEADERBOARD_TOP_N
) => {
    if (!boardId) {
        return { entries: [], myEntry: null };
    }

    try {
        const result = await callable("listLeaderboard")({
            boardId,
            topN,
        });
        const data = result?.data || {};
        const entries = (Array.isArray(data.entries) ? data.entries : [])
            .map((entry) =>
                normalizeLeaderboardEntry(entry?.uid || entry?.id, entry)
            )
            .filter(Boolean);
        const myRaw = data.myEntry;
        const myEntry = myRaw
            ? normalizeLeaderboardEntry(myRaw.uid || uid, myRaw)
            : null;

        return { entries, myEntry };
    } catch (error) {
        if (!isCallableMissingOrUnavailable(error)) {
            throw new Error(getLeaderboardLoadErrorMessage(error));
        }

        console.warn(
            "listLeaderboard callable unavailable; reading Firestore instead.",
            error
        );
    }

    try {
        const [entries, myEntry] = await Promise.all([
            listLeaderboardEntries(boardId, topN),
            getMyLeaderboardEntry(uid, boardId),
        ]);
        return { entries, myEntry };
    } catch (error) {
        throw new Error(getLeaderboardLoadErrorMessage(error));
    }
};

/**
 * Write the friend-readable public profile snapshot for the signed-in user.
 * Requires a valid GID already assigned by ensureUserGid.
 */
export const syncUserPublicProfile = async (uid, profileInput = {}) => {
    if (!uid) {
        return null;
    }

    const payload = buildPublicProfile({
        uid,
        ...profileInput,
    });

    if (!payload || !isValidGid(payload.gid)) {
        return null;
    }

    const ref = doc(db, "publicProfiles", uid);
    const { updatedAtClient, gidDisplay, ...firestorePayload } = payload;

    await setDoc(
        ref,
        {
            ...firestorePayload,
            updatedAtClient,
            updatedAt: serverTimestamp(),
        },
        { merge: true }
    );

    return payload;
};

export const getUserHighScore = async (uid) => {
    if (!uid) return 0;

    const ref = doc(db, "users", uid);
    const snap = await getDoc(ref);
    if (!snap.exists()) return 0;

    const data = snap.data();
    return typeof data.topScore === "number" ? data.topScore : 0;
};

export const setUserHighScore = async (uid, topScore) => {
    if (!uid) return;

    const ref = doc(db, "users", uid);
    await setDoc(
        ref,
        {
            topScore,
            updatedAt: serverTimestamp(),
        },
        { merge: true }
    );
};

export const getUserCollection = async (uid) => {
    if (!uid) return [];

    const ref = doc(db, "users", uid);
    const snap = await getDoc(ref);
    if (!snap.exists()) return [];

    const data = snap.data();
    const arr = Array.isArray(data.collectionUnlocked) ? data.collectionUnlocked : [];
    // Normalize to numbers when possible
    return arr
        .map((x) => {
            const n = Number(x);
            return Number.isFinite(n) ? n : x;
        })
        .filter((x) => x !== null && x !== undefined);
};

export const setUserCollection = async (uid, collectionUnlocked) => {
    if (!uid) return;

    const ref = doc(db, "users", uid);
    await setDoc(
        ref,
        {
            collectionUnlocked: Array.isArray(collectionUnlocked)
                ? collectionUnlocked
                : [],
            updatedAt: serverTimestamp(),
        },
        { merge: true }
    );
};

export const getUserFoilCollection = async (uid) => {
    if (!uid) return [];

    const ref = doc(db, "users", uid);
    const snap = await getDoc(ref);
    if (!snap.exists()) return [];

    const data = snap.data();
    const arr = Array.isArray(data.collectionFoilUnlocked)
        ? data.collectionFoilUnlocked
        : [];

    return arr
        .map((x) => {
            const n = Number(x);
            return Number.isFinite(n) ? n : x;
        })
        .filter((x) => x !== null && x !== undefined);
};

export const setUserFoilCollection = async (uid, collectionFoilUnlocked) => {
    if (!uid) return;

    const ref = doc(db, "users", uid);
    await setDoc(
        ref,
        {
            collectionFoilUnlocked: Array.isArray(collectionFoilUnlocked)
                ? collectionFoilUnlocked
                : [],
            updatedAt: serverTimestamp(),
        },
        { merge: true }
    );
};

export const getUserProfile = async (uid) => {
    if (!uid) {
        return null;
    }

    const ref = doc(db, "users", uid);
    const snap = await getDoc(ref);

    if (!snap.exists()) {
        return null;
    }

    const data = snap.data();

    return {
        username: typeof data.username === "string" ? data.username : "",
        profileImageId:
            data.profileImageId !== null && data.profileImageId !== undefined
                ? data.profileImageId
                : null,
        gamesPlayed:
            typeof data.gamesPlayed === "number" ? data.gamesPlayed : 0,
        gid: isValidGid(data.gid) ? Number(data.gid) : null,
        cosmetics:
            data.cosmetics && typeof data.cosmetics === "object"
                ? data.cosmetics
                : null,
    };
};

export const getUserCosmetics = async (uid) => {
    if (!uid) {
        return null;
    }

    const profile = await getUserProfile(uid);
    return profile?.cosmetics || null;
};

export const setUserCosmetics = async (uid, cosmetics) => {
    if (!uid || !cosmetics || typeof cosmetics !== "object") {
        return;
    }

    const ref = doc(db, "users", uid);
    await setDoc(
        ref,
        {
            cosmetics,
            updatedAt: serverTimestamp(),
        },
        { merge: true }
    );
};

export const setUserProfile = async (uid, profileData) => {
    if (!uid) {
        return;
    }

    const ref = doc(db, "users", uid);

    await setDoc(
        ref,
        {
            ...profileData,
            updatedAt: serverTimestamp(),
        },
        { merge: true }
    );
};

// path: src/firebaseUtils.js

export const getUserHearts = async (uid) => {
    if (!uid) {
        return null;
    }

    const ref = doc(db, "users", uid);
    const snap = await getDoc(ref);

    if (!snap.exists()) {
        return null;
    }

    const data = snap.data();

    const hearts = Number(data.hearts);
    const heartLastRefillAt = Number(data.heartLastRefillAt);

    if (!Number.isFinite(hearts) || !Number.isFinite(heartLastRefillAt)) {
        return null;
    }

    return {
        hearts,
        heartLastRefillAt,
    };
};

export const setUserHearts = async (uid, heartData) => {
    if (!uid) {
        return;
    }

    const ref = doc(db, "users", uid);

    await setDoc(
        ref,
        {
            hearts: Number(heartData?.hearts),
            heartLastRefillAt: Number(heartData?.heartLastRefillAt),
            updatedAt: serverTimestamp(),
        },
        { merge: true }
    );
};

export const getUserTreats = async (uid) => {
    if (!uid) {
        return null;
    }

    const ref = doc(db, "users", uid);
    const snap = await getDoc(ref);

    if (!snap.exists()) {
        return null;
    }

    const data = snap.data() || {};
    const treats = Number(data.treats);

    if (!Number.isFinite(treats)) {
        return null;
    }

    return {
        treats: Math.max(0, Math.floor(treats)),
    };
};

export const setUserTreats = async (uid, treats) => {
    if (!uid) {
        return;
    }

    const ref = doc(db, "users", uid);
    await setDoc(
        ref,
        {
            treats: Math.max(0, Math.floor(Number(treats) || 0)),
            updatedAt: serverTimestamp(),
        },
        { merge: true }
    );
};

export const getUserInventory = async (uid) => {
    if (!uid) {
        return null;
    }

    const ref = doc(db, "users", uid);
    const snap = await getDoc(ref);

    if (!snap.exists()) {
        return null;
    }

    const data = snap.data() || {};
    const inventory = data.inventory;

    if (!inventory || typeof inventory !== "object") {
        return null;
    }

    return inventory;
};

export const setUserInventory = async (uid, inventory) => {
    if (!uid || !inventory || typeof inventory !== "object") {
        return;
    }

    const ref = doc(db, "users", uid);
    await setDoc(
        ref,
        {
            inventory,
            updatedAt: serverTimestamp(),
        },
        { merge: true }
    );
};

export const getUserIapEntitlements = async (uid) => {
    if (!uid) {
        return null;
    }

    const ref = doc(db, "users", uid);
    const snap = await getDoc(ref);

    if (!snap.exists()) {
        return null;
    }

    const data = snap.data();
    const entitlements = data.iapEntitlements;

    if (!entitlements || typeof entitlements !== "object") {
        return null;
    }

    return entitlements;
};

export const setUserIapEntitlements = async (uid, entitlements) => {
    if (!uid || !entitlements || typeof entitlements !== "object") {
        return;
    }

    const ref = doc(db, "users", uid);
    await setDoc(
        ref,
        {
            iapEntitlements: entitlements,
            updatedAt: serverTimestamp(),
        },
        { merge: true }
    );
};

export const getUserRedemptions = async (uid) => {
    if (!uid) {
        return null;
    }

    const ref = doc(db, "users", uid);
    const snap = await getDoc(ref);
    if (!snap.exists()) {
        return null;
    }

    const data = snap.data();
    const redemptions = data.redemptions;
    return redemptions && typeof redemptions === "object" ? redemptions : null;
};

export const setUserRedemptions = async (uid, redemptions) => {
    if (!uid || !redemptions || typeof redemptions !== "object") {
        return;
    }

    const ref = doc(db, "users", uid);
    await setDoc(
        ref,
        {
            redemptions,
            updatedAt: serverTimestamp(),
        },
        { merge: true }
    );
};

export const getUserStoryProgress = async (uid) => {
    if (!uid) {
        return null;
    }

    const ref = doc(db, "users", uid);
    const snap = await getDoc(ref);

    if (!snap.exists()) {
        return null;
    }

    const data = snap.data();
    const storyProgress = data.storyProgress;

    if (!storyProgress || typeof storyProgress !== "object") {
        return null;
    }

    return storyProgress;
};

export const setUserStoryProgress = async (uid, storyProgress) => {
    if (!uid || !storyProgress || typeof storyProgress !== "object") {
        return;
    }

    const ref = doc(db, "users", uid);

    await setDoc(
        ref,
        {
            storyProgress: {
                ...storyProgress,
                updatedAtClient: Date.now(),
            },
            updatedAt: serverTimestamp(),
        },
        { merge: true }
    );
};

export const getUserEndlessStats = async (uid) => {
    if (!uid) return null;

    const ref = doc(db, "users", uid);
    const snap = await getDoc(ref);
    if (!snap.exists()) return null;

    const data = snap.data();
    return data.endlessStats && typeof data.endlessStats === "object"
        ? data.endlessStats
        : null;
};

export const setUserEndlessStats = async (uid, endlessStats) => {
    if (!uid || !endlessStats || typeof endlessStats !== "object") {
        return;
    }

    const ref = doc(db, "users", uid);
    await setDoc(
        ref,
        {
            endlessStats,
            updatedAt: serverTimestamp(),
        },
        { merge: true }
    );
};